import React, { useCallback, useContext, useMemo } from 'react';
import {
  AppTrackingEvent,
  TrackingDispatchContextType,
  TrackingDispatchProviderProps,
} from './types';

const TrackingDispatchContext = React.createContext<
  TrackingDispatchContextType | undefined
>(undefined);

export const TrackingDispatchProvider: React.FC<
  TrackingDispatchProviderProps
> = ({ children, dispatch, dispatchMergeStrategy = 'prepend' }) => {
  const parentContext = useTrackingDispatchUnsafe();
  const parentDispatch = parentContext?.dispatch;
  const dispatchers = useMemo(
    () => (Array.isArray(dispatch) ? dispatch : [dispatch]),
    [dispatch],
  );

  const wrappedDispatch = useCallback(
    (event: AppTrackingEvent) => {
      const myDispatch = () => dispatchers.forEach((d) => d(event));

      if (dispatchMergeStrategy === 'append') {
        parentDispatch?.(event);
        myDispatch();
      } else if (dispatchMergeStrategy === 'prepend') {
        myDispatch();
        parentDispatch?.(event);
      } else {
        myDispatch();
      }
    },
    [dispatchMergeStrategy, dispatchers, parentDispatch],
  );

  return (
    <TrackingDispatchContext.Provider
      value={useMemo(() => ({ dispatch: wrappedDispatch }), [wrappedDispatch])}
    >
      {children}
    </TrackingDispatchContext.Provider>
  );
};

export function useTrackingDispatch() {
  const context = useContext(TrackingDispatchContext);

  if (context === undefined) {
    throw new Error(
      'useTrackingDispatch must be used within TrackingDispatchProvider',
    );
  }

  return context;
}

function useTrackingDispatchUnsafe() {
  try {
    return useTrackingDispatch();
  } catch {
    return undefined;
  }
}
